import { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  Paper,
  Stack,
  Tab,
} from "@mui/material";
import { Check } from "@mui/icons-material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@/app/hooks";
import { useGetAzureBoardsProjectsQuery } from "@/features/jiraProject/api";
import IOSSwitch from "../IOSSwitch";
import type { IntegrationState } from "../types";

interface AzureConfigProps {
  projectDetails: any;
  state: IntegrationState;
  isAzureConfigured: boolean;
  handleAzureProjectChange: (event: any, newValue: any) => void;
  updateAzureFieldConfig: (
    projectId: string,
    fieldKey: string,
    value?: string | boolean,
    dataType?: string,
  ) => void;
  handleAzureOrgChange: (value: string) => void;
  onNavigateToCompanySettings: () => void;
}

export default function AzureConfig({
  projectDetails,
  state,
  isAzureConfigured,
  handleAzureProjectChange,
  updateAzureFieldConfig,
  handleAzureOrgChange,
  onNavigateToCompanySettings,
}: AzureConfigProps) {
  const { t } = useTranslation(["bugherd"]);
  const [azureTabValue, setAzureTabValue] = useState(1);
  const { companyDetails } = useAppSelector((s) => s.appGlobal);

  const { data: azureProjectsData, isLoading: isLoadingAzureProjects } =
    useGetAzureBoardsProjectsQuery(
      { companyId: companyDetails?.companyId },
      { skip: !companyDetails?.companyId },
    );
  const azureProjects =
    azureProjectsData?.data?.decryptedData?.value ||
    azureProjectsData?.data?.decryptedData ||
    [];

  // Switching integration warning
  const existingIntegration = projectDetails?.integrationType;
  const showProjectWarning =
    existingIntegration &&
    existingIntegration !== "NONE" &&
    existingIntegration !== "AZURE";

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 2.5,
        borderRadius: "10px",
        border: (theme) => `1px solid ${theme.palette.divider}`,
        bgcolor: (theme) => theme.palette.background.paper,
      }}
    >
      <Typography
        variant="subtitle2"
        fontWeight={600}
        fontSize="13px"
        mb={2}
        color="text.secondary"
      >
        {t("Azure Integration")}
      </Typography>

      {/* Warning when switching Project Management integration */}
      {showProjectWarning && (() => {
        const existingName =
          existingIntegration === "JIRA"
            ? "Jira"
            : existingIntegration === "AZURE"
              ? "Azure Boards"
              : "Asana";

        return (
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: "8px",
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 152, 0, 0.1)"
                  : "#fff3e0",
              border: (theme) =>
                `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 152, 0, 0.3)" : "#ffcc80"}`,
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={1.5}>
              <Box flex={1}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <WarningAmberIcon
                    sx={{ color: "#f57c00", fontSize: 18 }}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    fontSize="13px"
                    color="#e65100"
                  >
                    {t("Switching Project Management Integration")}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  fontSize="12px"
                  color="text.secondary"
                  lineHeight={1.5}
                >
                  {t("switchingIntegrationWarning", {
                    existing: existingName,
                    new: "Azure Boards",
                  })}
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })()}

      <Box display="flex" gap={2} mb={2}>
        <TextField
          label={t("Azure Organization")}
          fullWidth
          variant="outlined"
          value={state.azureBoardsConfig?.[0]?.organization || ""}
          required
          onChange={(e) => handleAzureOrgChange(e.target.value)}
          disabled={!isAzureConfigured}
        />
      </Box>

      <Autocomplete
        options={Array.isArray(azureProjects) ? azureProjects : []}
        getOptionLabel={(option: any) => option?.name || ""}
        loading={isLoadingAzureProjects}
        value={
          state.azureBoardsConfig?.length > 0 &&
          Array.isArray(azureProjects) &&
          azureProjects.length > 0
            ? azureProjects.find(
                (p: any) =>
                  p?.id === state.azureBoardsConfig[0]?.projectId,
              ) || null
            : null
        }
        onChange={handleAzureProjectChange}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Typography sx={{ flexGrow: 1 }}>{option?.name}</Typography>
              {selected && (
                <Check
                  sx={{
                    color: (theme) => theme.palette.primary.main,
                    ml: 1,
                  }}
                />
              )}
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("Select Azure Project")}
            placeholder={t("Select Azure Project")}
            required
          />
        )}
        disabled={!isAzureConfigured}
      />

      {!isAzureConfigured && (
        <Box
          sx={{
            mt: 1,
            mb: 1,
            p: 1.5,
            borderRadius: "6px",
            bgcolor: (theme) => `${theme.palette.error.main}08`,
            border: (theme) =>
              `1px solid ${theme.palette.error.main}30`,
          }}
        >
          <Typography
            variant="caption"
            color="error"
            sx={{ fontSize: "11.5px" }}
          >
            {t("Azure Boards integration is not configured. Click")}{" "}
            <span
              onClick={onNavigateToCompanySettings}
              style={{
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: 600,
              }}
            >
              {t("here")}
            </span>{" "}
            {t("to integrate.")}
          </Typography>
        </Box>
      )}

      {/* Azure Configuration Tabs */}
      {state.azureBoardsConfig?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <TabContext value={azureTabValue.toString()}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <TabList
                onChange={(_, newValue) => setAzureTabValue(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 36,
                  "& .MuiTab-root": {
                    minHeight: 36,
                    fontSize: "12.5px",
                    textTransform: "none",
                    fontWeight: 500,
                    py: 0.5,
                  },
                }}
              >
                {state.azureBoardsConfig?.map((config, index) => (
                  <Tab
                    key={config.projectId}
                    label={
                      config.projectData?.name || config.projectName
                    }
                    value={(index + 1).toString()}
                  />
                ))}
              </TabList>
            </Box>

            {state.azureBoardsConfig?.map((config, index) => (
              <TabPanel
                key={config.projectId}
                value={(index + 1).toString()}
                sx={{ px: 0.5, py: 1.5 }}
              >
                {/* Sync Configuration Section */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: "8px",
                    border: (theme) =>
                      `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    fontSize="12.5px"
                    mb={1.5}
                    color="text.secondary"
                  >
                    {t("Sync Configuration")}
                  </Typography>

                  <Stack spacing={1.5}>
                    {/* Sync Tasks to Azure */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography variant="body2">
                          {t("Automatically create work item in Azure")}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {t(
                            "When a task is created, it will be automatically synced to Azure Boards",
                          )}
                        </Typography>
                      </Box>
                      <IOSSwitch
                        checked={
                          config.fieldConfig?.isAutomaticCreateAzure ||
                          false
                        }
                        onChange={(e: any) =>
                          updateAzureFieldConfig(
                            config.projectId,
                            "isAutomaticCreateAzure",
                            e.target.checked,
                            "boolean",
                          )
                        }
                      />
                    </Box>

                    {/* Sync Comments to Azure */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography variant="body2">
                          {t("Sync comments to Azure")}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {t(
                            "Comments added here will be synced to Azure Boards",
                          )}
                        </Typography>
                      </Box>
                      <IOSSwitch
                        checked={
                          config.fieldConfig?.isCreateComment || false
                        }
                        onChange={(e: any) =>
                          updateAzureFieldConfig(
                            config.projectId,
                            "isCreateComment",
                            e.target.checked,
                            "boolean",
                          )
                        }
                      />
                    </Box>

                    {/* Sync Comments from Azure */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography variant="body2">
                          {t("Sync comments from Azure")}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {t(
                            "Comments from Azure Boards will be synced here",
                          )}
                        </Typography>
                      </Box>
                      <IOSSwitch
                        checked={
                          config.fieldConfig?.isSyncComment || false
                        }
                        onChange={(e: any) =>
                          updateAzureFieldConfig(
                            config.projectId,
                            "isSyncComment",
                            e.target.checked,
                            "boolean",
                          )
                        }
                      />
                    </Box>

                    {/* Sync from Azure */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography variant="body2">
                          {t("Sync work items from Azure")}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {t(
                            "Work items created in Azure Boards will be synced here",
                          )}
                        </Typography>
                      </Box>
                      <IOSSwitch
                        checked={
                          config.fieldConfig?.syncFromAzure || false
                        }
                        onChange={(e: any) =>
                          updateAzureFieldConfig(
                            config.projectId,
                            "syncFromAzure",
                            e.target.checked,
                            "boolean",
                          )
                        }
                      />
                    </Box>
                  </Stack>
                </Paper>
              </TabPanel>
            ))}
          </TabContext>
        </Box>
      )}
    </Paper>
  );
}
